import React from 'react';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SchedulingAlertTone } from '@/utils/bookingErrorMessage';

const TONE_STYLES: Record<
  SchedulingAlertTone,
  {
    wrap: string;
    iconWrap: string;
    icon: string;
    title: string;
    body: string;
    hint: string;
  }
> = {
  warning: {
    wrap: 'border-warning-200 bg-warning-50',
    iconWrap: 'bg-warning-100',
    icon: 'text-warning-600',
    title: 'text-warning-800',
    body: 'text-warning-800',
    hint: 'text-warning-700/80',
  },
  danger: {
    wrap: 'border-danger-200 bg-danger-50',
    iconWrap: 'bg-danger-100',
    icon: 'text-danger-600',
    title: 'text-danger-800',
    body: 'text-danger-800',
    hint: 'text-danger-700/80',
  },
};

function ToneIcon({
  tone,
  className,
}: {
  tone: SchedulingAlertTone;
  className?: string;
}) {
  if (tone === 'warning') {
    return <AlertTriangle className={className} />;
  }
  return <AlertCircle className={className} />;
}

export interface BookingAlertProps {
  title: string;
  message: string;
  tone?: SchedulingAlertTone;
  hint?: string;
  className?: string;
  children?: React.ReactNode;
}

/**
 * Inline constraint/error callout for booking flows. Title + body so operators
 * see what happened and what to do, instead of a raw error code in a thin strip.
 */
export const BookingAlert: React.FC<BookingAlertProps> = ({
  title,
  message,
  tone = 'danger',
  hint,
  className,
  children,
}) => {
  const styles = TONE_STYLES[tone];

  return (
    <div
      role="alert"
      className={cn('rounded-xl border px-4 py-3 flex items-start gap-3', styles.wrap, className)}
    >
      <div
        className={cn(
          'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5',
          styles.iconWrap,
        )}
      >
        <ToneIcon tone={tone} className={cn('h-3.5 w-3.5', styles.icon)} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={cn('text-sm font-semibold leading-snug', styles.title)}>{title}</p>
        <p className={cn('text-xs leading-relaxed mt-0.5', styles.body)}>{message}</p>
        {hint && <p className={cn('text-11 leading-relaxed mt-1', styles.hint)}>{hint}</p>}
        {children}
      </div>
    </div>
  );
};
